// REST client for the FastAPI server. Pure transport — no crypto here.

import type { LoginResponse, ServerItem, ServerSetupStatus } from "../types";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export class ApiClient {
  constructor(
    private serverUrl: string,
    private getAccessToken: () => string | null = () => null,
    /** Called on 401 from an authenticated endpoint. Should refresh the access
     * token (typically using a stored refresh token) and return true if a retry
     * is worth attempting. Wired up by the SW / PWA Session so ApiClient itself
     * stays unaware of where tokens live. */
    private onUnauthorized?: () => Promise<boolean>,
  ) {}

  setServerUrl(url: string) {
    this.serverUrl = url.replace(/\/$/, "");
  }

  private async doFetch(path: string, init: RequestInit, authed: boolean): Promise<Response> {
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");
    if (authed) {
      const tok = this.getAccessToken();
      if (tok) headers.set("Authorization", `Bearer ${tok}`);
    }
    return await fetch(`${this.serverUrl}${path}`, { ...init, headers });
  }

  private async request<T>(
    path: string,
    init: RequestInit = {},
    authed = true,
  ): Promise<T> {
    let res: Response;
    try {
      res = await this.doFetch(path, init, authed);
    } catch (e) {
      throw new ApiError(
        0,
        `nie można połączyć się z ${this.serverUrl} — sprawdź czy serwer działa ` +
          `i adres jest poprawny (cause: ${(e as Error).message})`,
      );
    }
    // Auto-refresh on 401 for authenticated requests, exactly once.
    if (res.status === 401 && authed && this.onUnauthorized) {
      const refreshed = await this.onUnauthorized().catch(() => false);
      if (refreshed) {
        try {
          res = await this.doFetch(path, init, authed);
        } catch (e) {
          throw new ApiError(0, `network error after refresh: ${(e as Error).message}`);
        }
      }
    }
    if (!res.ok) {
      let detail = "";
      try {
        const body = await res.json();
        detail = body?.detail ?? JSON.stringify(body);
      } catch {
        detail = res.statusText;
      }
      throw new ApiError(res.status, `${res.status} ${detail}`);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  // ---- setup ----
  getSetupStatus(): Promise<ServerSetupStatus> {
    return this.request<ServerSetupStatus>("/setup/status", { method: "GET" }, false);
  }

  setup(body: {
    kdf_salt_b64: string;
    kdf_opslimit: number;
    kdf_memlimit: number;
    auth_hash_b64: string;
    encrypted_vault_key: { ciphertext_b64: string; nonce_b64: string };
  }): Promise<{ status: string }> {
    return this.request<{ status: string }>(
      "/setup",
      { method: "POST", body: JSON.stringify(body) },
      false,
    );
  }

  // ---- auth ----
  login(authHashB64: string): Promise<LoginResponse> {
    return this.request<LoginResponse>(
      "/auth/login",
      { method: "POST", body: JSON.stringify({ auth_hash_b64: authHashB64 }) },
      false,
    );
  }

  refresh(refreshToken: string): Promise<{ access_token: string; refresh_token: string }> {
    return this.request(
      "/auth/refresh",
      { method: "POST", body: JSON.stringify({ refresh_token: refreshToken }) },
      false,
    );
  }

  changePassword(body: {
    old_auth_hash_b64: string;
    new_auth_hash_b64: string;
    new_encrypted_vault_key: { ciphertext_b64: string; nonce_b64: string };
    new_kdf_salt_b64?: string | null;
  }): Promise<{ status: string }> {
    return this.request<{ status: string }>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  // ---- vault ----
  listItems(includeDeleted = false): Promise<ServerItem[]> {
    const qs = includeDeleted ? "?include_deleted=true" : "";
    return this.request<ServerItem[]>(`/vault/items${qs}`);
  }

  sync(since?: string): Promise<ServerItem[]> {
    const qs = since ? `?since=${encodeURIComponent(since)}` : "";
    return this.request<ServerItem[]>(`/vault/sync${qs}`);
  }

  createItem(body: {
    id: string;
    crypto_version: number;
    ciphertext_b64: string;
    nonce_b64: string;
  }): Promise<ServerItem> {
    return this.request<ServerItem>("/vault/items", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateItem(
    id: string,
    body: { ciphertext_b64: string; nonce_b64: string; crypto_version: number },
  ): Promise<ServerItem> {
    return this.request<ServerItem>(`/vault/items/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  deleteItem(id: string): Promise<void> {
    return this.request(`/vault/items/${id}`, { method: "DELETE" });
  }
}
